import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { getContracts, getAssets } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertTriangle, Plus, Upload, Send } from 'lucide-react';
import { toast } from 'sonner';

export const DamageReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    contract_id: '',
    asset_id: '',
    description: '',
    estimated_cost: '',
    damage_type: 'body',
    severity: 'minor',
    client_acknowledged: false,
    notes: ''
  });

  const damageTypes = [
    { value: 'body', label: 'Body Damage' },
    { value: 'mechanical', label: 'Mechanical Issue' },
    { value: 'interior', label: 'Interior Damage' },
    { value: 'tire', label: 'Tire/Wheel Damage' },
    { value: 'windshield', label: 'Windshield/Glass' },
    { value: 'other', label: 'Other' }
  ];

  const severityLevels = [
    { value: 'minor', label: 'Minor', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'moderate', label: 'Moderate', color: 'bg-orange-100 text-orange-800' },
    { value: 'severe', label: 'Severe', color: 'bg-red-100 text-red-800' }
  ];

  useEffect(() => {
    loadContracts();
    loadAssets();
    loadReports();
  }, []);

  const loadContracts = async () => {
    try {
      const response = await getContracts(null, 'active');
      setContracts(response.data);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    }
  };

  const loadAssets = async () => {
    try {
      const response = await getAssets();
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/damage-reports');
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load damage reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        estimated_cost: parseFloat(formData.estimated_cost)
      };
      await api.post('/damage-reports', data);
      toast.success('Damage report created! Email notification sent to client.');
      setDialogOpen(false);
      loadReports();
      setFormData({
        contract_id: '',
        asset_id: '',
        description: '',
        estimated_cost: '',
        damage_type: 'body',
        severity: 'minor',
        client_acknowledged: false,
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to create damage report');
    }
  };

  const handleGenerateInvoice = async (reportId) => {
    try {
      await api.post(`/damage-reports/${reportId}/generate-invoice`);
      toast.success('Back-charge invoice generated!');
      loadReports();
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const totalCost = reports.reduce((sum, r) => sum + (r.estimated_cost || 0), 0);
  const pendingAcknowledgement = reports.filter(r => !r.client_acknowledged).length;
  const backCharged = reports.filter(r => r.back_charged).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="damage-reports-title">Damage Reports</h1>
          <p className="text-muted-foreground mt-1">{reports.length} total reports</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="add-report-button">
              <Plus className="h-4 w-4 mr-2" />
              New Damage Report
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Damage Report</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contract *</Label>
                  <Select value={formData.contract_id} onValueChange={(v) => setFormData({ ...formData, contract_id: v })} required>
                    <SelectTrigger data-testid="contract-select">
                      <SelectValue placeholder="Select contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {contracts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.contract_number} - {c.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Asset/Vehicle *</Label>
                  <Select value={formData.asset_id} onValueChange={(v) => setFormData({ ...formData, asset_id: v })} required>
                    <SelectTrigger data-testid="asset-select">
                      <SelectValue placeholder="Select asset" />
                    </SelectTrigger>
                    <SelectContent>
                      {assets.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.registration_number} - {a.make} {a.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Damage Type *</Label>
                  <Select value={formData.damage_type} onValueChange={(v) => setFormData({ ...formData, damage_type: v })} required>
                    <SelectTrigger data-testid="damage-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {damageTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity *</Label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })} required>
                    <SelectTrigger data-testid="severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {severityLevels.map((level) => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="description-input"
                  placeholder="Describe the damage in detail"
                  rows={3}
                />
              </div>
              <div>
                <Label>Estimated Repair Cost (SAR) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                  required
                  data-testid="cost-input"
                />
              </div>
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="notes-input"
                  placeholder="Any additional information"
                  rows={2}
                />
              </div>
              <div className="bg-blue-50 p-4 rounded text-sm">
                <div className="font-semibold mb-2">📸 Photo Upload</div>
                <div className="text-muted-foreground">
                  Photo upload feature coming soon. For now, photos can be attached via email or stored in shared drive.
                  Mobile app will allow field staff to capture and upload photos directly.
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">Cancel</Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-report-button">
                  <Send className="h-4 w-4 mr-2" />
                  Create & Email Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Reports</div>
          <div className="text-3xl font-bold" data-testid="total-reports">{reports.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Estimated Cost</div>
          <div className="text-3xl font-bold text-red-600" data-testid="total-cost">{totalCost.toLocaleString()} SAR</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Pending Acknowledgement</div>
          <div className="text-3xl font-bold text-amber-600" data-testid="pending-ack">{pendingAcknowledgement}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Back-Charged</div>
          <div className="text-3xl font-bold text-green-600" data-testid="back-charged">{backCharged}</div>
        </Card>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading damage reports...</div>
        </div>
      ) : reports.length === 0 ? (
        <Card className="p-12 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No damage reports yet</h3>
          <p className="text-muted-foreground mb-4">Track vehicle damages and back-charge clients</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {reports.map((report) => {
            const contract = contracts.find(c => c.id === report.contract_id);
            const asset = assets.find(a => a.id === report.asset_id);
            const severity = severityLevels.find(s => s.value === report.severity);
            
            return (
              <Card key={report.id} className="p-6" data-testid={`report-card-${report.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold">{asset?.registration_number || 'N/A'}</h3>
                      <p className="text-sm text-muted-foreground">{contract?.client_name || 'N/A'}</p>
                    </div>
                  </div>
                  <Badge className={severity?.color}>{severity?.label}</Badge>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Damage Type:</span>
                    <span className="ml-2 font-semibold">
                      {damageTypes.find(t => t.value === report.damage_type)?.label}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Description:</span>
                    <p className="mt-1 text-xs">{report.description}</p>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">Estimated Cost:</span>
                    <span className="font-bold text-lg text-red-600">{report.estimated_cost.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      {report.client_acknowledged ? (
                        <Badge className="bg-green-100 text-green-800">Client Acknowledged</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800">Pending Acknowledgement</Badge>
                      )}
                    </div>
                    {report.back_charged && (
                      <Badge className="bg-blue-100 text-blue-800">Invoice Generated</Badge>
                    )}
                  </div>
                  {!report.back_charged && report.client_acknowledged && (
                    <Button
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700 mt-2"
                      onClick={() => handleGenerateInvoice(report.id)}
                      data-testid={`generate-invoice-${report.id}`}
                    >
                      Generate Back-Charge Invoice
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
